import React from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import { getBackgroundTheme, getPatternModal, getTextColor, getBorders } from '../../../style';
import { css } from 'glamor';
import ModalPattern from '../ModalPattern';
import { SvgIcon } from '@material-ui/core';
import { CancelRounded } from '@material-ui/icons';
import styled from '@emotion/styled';
import { useDisableBodyScroll } from '../../../hooks/useDisableBodyScroll';
import { MODAL_MAX_HEIGHT_VALUE } from '../../../constants/modal';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import breakpoints from 'theme/material/breakpoints';

const CloseBtn = styled('button')({
  marginLeft: 'auto',
  appearance: 'none',
  border: 'none',
  background: 'none',
  fontSize: 0,
  padding: 0,
});

const closeButton = {
  fontSize: 21,
  color: '#8F8F8F',
};

const getModalStyles = () => ({
  background: getPatternModal().bgGradientDark,
  position: 'relative',
  boxSizing: 'border-box',
  border: getPatternModal().borderStyle,
  outline: 'none',
  maxHeight: `${MODAL_MAX_HEIGHT_VALUE}vh`,
  overflow: 'auto',
  ...getTextColor().DARK,
  ':focus': {
    outline: 'none',
  },

  [breakpoints.down(639.05)]: {
    height: '100%',
  },

  [breakpoints.up(640)]: {
    borderRadius: 10,
    position: 'relative',
    top: 40,
    maxWidth: 600,
    margin: '32px auto',
  },
});

const getModalWithoutPatternStyles = () => ({
  ...getModalStyles(),
  background: getBackgroundTheme().block,
  border: getBorders().DEFAULT,
  maxHeight: `${MODAL_MAX_HEIGHT_VALUE}vh`,
  overflow: 'auto',
});

const OverlayStyles = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 10000,
  backgroundColor: 'rgba(0,0,0,0.65)',
  overflow: 'auto',
  [breakpoints.down(639.05)]: {
    backgroundColor: '#fff',
  },
};

ReactModal.setAppElement('body');

export interface ModalWithPatternProps {
  dataTestID: string;
  isModalOpen: boolean;
  modalContentLabel: string;
  closeOnOverlayClick: boolean;
  closeRequest(): void;
  closeBtnClassName: string;
  closeBtnDataTestId: string;
  onCloseClick(): void;
  children: React.ReactNode;
  customOverlayStyle?: Record<string, unknown>;
  customContentStyle?: Record<string, unknown>;
  hideCloseBtn?: boolean;
  customRef?: React.ReactNode;
  withoutBgPattern?: boolean;
  modalPatternPadding?: number;
  modalRef?: any;
  enableTouchMove?: boolean;
  customContentBodyStyle?: React.CSSProperties;
  customCloseIconStyle?: React.CSSProperties;
  customCloseBtnStyle?: React.CSSProperties;
}

const ModalWithPattern = ({
  dataTestID,
  isModalOpen,
  modalContentLabel,
  closeOnOverlayClick = true,
  closeRequest,
  closeBtnClassName,
  closeBtnDataTestId,
  onCloseClick,
  customOverlayStyle = {},
  customContentStyle = {},
  hideCloseBtn = false,
  children,
  customRef,
  withoutBgPattern = false,
  enableTouchMove = true,
  customContentBodyStyle,
  customCloseIconStyle = {},
  customCloseBtnStyle = {},
}: ModalWithPatternProps): JSX.Element => {
  useDisableBodyScroll(isModalOpen, enableTouchMove);
  const { isMobile } = useMediaBreakpoints();

  return (
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    <ReactModal
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      ref={customRef}
      className={`${css(withoutBgPattern ? getModalWithoutPatternStyles() : getModalStyles())}`}
      overlayClassName={`${css(OverlayStyles)}`}
      data-test-id={dataTestID}
      isOpen={isModalOpen}
      contentLabel={modalContentLabel}
      shouldCloseOnOverlayClick={closeOnOverlayClick}
      onRequestClose={closeRequest}
      style={{
        content: customContentStyle,
        overlay: customOverlayStyle,
      }}
    >
      <ModalPattern
        padding={isMobile ? '15px 15px 30px 15px' : 30}
        customStyle={{ display: 'flex', flexDirection: 'column', alignItems: 'center', ...customContentBodyStyle }}
      >
        {!hideCloseBtn && (
          <CloseBtn
            className={closeBtnClassName}
            data-test-id={closeBtnDataTestId}
            onClick={onCloseClick}
            style={customCloseBtnStyle}
          >
            <SvgIcon component={CancelRounded} style={{ ...closeButton, ...customCloseIconStyle }} />
          </CloseBtn>
        )}
        {children}
      </ModalPattern>
    </ReactModal>
  );
};

ModalWithPattern.propTypes = {
  contentLabel: PropTypes.string.isRequired,
  shouldOpen: PropTypes.bool,
  shouldCloseOnOverlayClick: PropTypes.bool,
  confirmationText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  cancelBtnText: PropTypes.string.isRequired,
  confirmationBtnText: PropTypes.string.isRequired,
  onCloseHandler: PropTypes.func,
  btnAlignment: PropTypes.string,
  className: PropTypes.string,
  hideCancelBtn: PropTypes.bool,
  hideConfirmBtn: PropTypes.bool,
  hideModalCloseBtn: PropTypes.bool,
  confirmationHeaderText: PropTypes.string,
  isConfirmBtnDisabled: PropTypes.bool,
};

ModalWithPattern.defaultProps = {
  confirmationHeaderText: '',
  confirmationText: 'Are you sure with this?',
  confirmationBtnText: 'Yes',
  cancelBtnText: 'No',
  shouldOpen: false,
  btnAlignment: 'right',
  shouldCloseOnOverlayClick: false,
  hideCancelBtn: false,
  hideConfirmBtn: false,
  hideModalCloseBtn: true,
  isConfirmBtnDisabled: false,
  contentLabel: '',
};

export default ModalWithPattern;
